import { Alert, Box, Grid, IconButton, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMemo, useState } from "react";
import { FieldData, PsaAnalysisResponse, PsaFieldResult } from "./types";

type Props = {
  analysis?: PsaAnalysisResponse | null;
  activeFieldKey?: string | null;
  onSelectField?: (field: PsaFieldResult) => void;
  data?: Record<string, FieldData> | null;
};

const toTitle = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const copyTextToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  textArea.style.pointerEvents = "none";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  const wasCopied = document.execCommand("copy");
  document.body.removeChild(textArea);

  if (!wasCopied) {
    throw new Error("Clipboard copy failed.");
  }
};

export default function ExtractedFieldsDisplay({ analysis = null, activeFieldKey = null, onSelectField, data = null }: Props) {
  const [search, setSearch] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const filteredFields = useMemo(() => {
    if (!analysis) return [];
    const query = search.trim().toLowerCase();
    if (!query) return analysis.fields;

    return analysis.fields.filter((field) =>
      [field.key, field.label, field.value, field.sourceText ?? ""].some((part) => part.toLowerCase().includes(query))
    );
  }, [analysis, search]);

  const filteredLegacyFields = useMemo(() => {
    if (!data) return [];
    const query = search.trim().toLowerCase();
    const entries = Object.entries(data);

    if (!query) {
      return entries;
    }

    return entries.filter(([fieldName, field]) =>
      [fieldName, field.value].some((part) => part.toLowerCase().includes(query))
    );
  }, [data, search]);

  if (!analysis && !data) {
    return null;
  }

  if (!analysis && data) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          AI can make mistakes. Review the extracted details against the source document before using them.
        </Alert>

        <Box>
          <Typography variant="h5" gutterBottom>
            {filteredLegacyFields.length} of {Object.keys(data).length} details
          </Typography>
        </Box>

        <TextField
          label="Search details"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          fullWidth
        />

        <Grid container spacing={2}>
          {filteredLegacyFields.map(([fieldName, field]) => (
            <Grid item xs={12} md={6} xl={4} key={fieldName}>
              <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {toTitle(fieldName)}
                      </Typography>
                      <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                        {field.value}
                      </Typography>
                    </Box>
                    <Tooltip title={copiedKey === fieldName ? "Copied" : "Copy value"}>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          await copyTextToClipboard(field.value);
                          setCopiedKey(fieldName);
                          window.setTimeout(() => setCopiedKey(null), 1200);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {field.page ? `Page ${field.page}` : "No page"}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  const currentAnalysis = analysis as PsaAnalysisResponse;

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        AI can make mistakes. Review the extracted details against the agreement before using them in Order Entry.
      </Alert>

      {currentAnalysis.warnings.map((warning) => (
        <Alert severity="info" key={warning}>
          {warning}
        </Alert>
      ))}

      <Box>
        <Typography variant="h5" gutterBottom>
          {filteredFields.length} of {currentAnalysis.fields.length} details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Reviewed in {currentAnalysis.openAiCallCount} pass{currentAnalysis.openAiCallCount === 1 ? "" : "es"}
        </Typography>
      </Box>

      <TextField
        label="Search details"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        size="small"
        fullWidth
      />

      <Grid container spacing={2}>
        {filteredFields.map((field) => {
          const isActive = activeFieldKey === field.key;
          return (
            <Grid item xs={12} key={`${field.key}-${field.pageNumber ?? 0}`}>
              <Paper
                elevation={isActive ? 6 : 2}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  border: isActive ? "2px solid #2563eb" : "1px solid #e5e7eb",
                  backgroundColor: isActive ? "#eff6ff" : "#fff",
                  height: "100%"
                }}
                onClick={async () => {
                  onSelectField?.(field);
                  await copyTextToClipboard(field.value);
                  setCopiedKey(field.key);
                  window.setTimeout(() => setCopiedKey(null), 1200);
                }}
              >
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {toTitle(field.label || field.key)}
                      </Typography>
                      <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                        {field.value}
                      </Typography>
                    </Box>
                    <Tooltip title={copiedKey === field.key ? "Copied" : "Copy value"}>
                      <IconButton
                        size="small"
                        onClick={async (event) => {
                          event.stopPropagation();
                          await copyTextToClipboard(field.value);
                          setCopiedKey(field.key);
                          window.setTimeout(() => setCopiedKey(null), 1200);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      {field.key}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {field.pageNumber ? `Page ${field.pageNumber}` : "No page"}
                    </Typography>
                  </Box>

                  {field.sourceText && (
                    <Typography variant="body2" color="primary" sx={{ wordBreak: "break-word" }}>
                      “{field.sourceText}”
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
}
